import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { CLASS_SESSION_DAYS_OF_WEEK } from "@/shared/lib/class-session-day-of-week";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
} from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker, cn } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const EMPTY_FILTERS: FilterConditionDTO[] = [];
export { EMPTY_FILTERS };

type RoomItem = { id: string; name: string };
type InstructorItem = { id: string; name: string };
type ClassTemplateItem = { id: string; name: string };

function getDayOfWeekFilterValues(
  filters: FilterConditionDTO[],
): number[] {
  const f = findCondition(filters, "dayOfWeek");
  if (!f || f.op !== "in" || !Array.isArray(f.value)) return [];
  return f.value.filter(
    (v): v is number => typeof v === "number" && v >= 1 && v <= 7,
  );
}

export function ClassSessionFilter() {
  const nav = useNavigate({ from: "/app/classes/sessions" });
  const search = useSearch({
    from: "/app/classes/sessions",
  }) as OffsetPageQueryDTO;
  const filters = useMemo(
    () => (search.filters as FilterConditionDTO[] | undefined) ?? EMPTY_FILTERS,
    [search.filters],
  );

  const templateFilter = findCondition(filters, "templateId");
  const classTypeFilter = findCondition(filters, "classTypeId");
  const levelFilter = findCondition(filters, "levelId");
  const roomFilter = findCondition(filters, "roomId");
  const instructorFilter = findCondition(filters, "instructorId");
  const statusFilter = findCondition(filters, "status");
  const dateFromFilter = filters.find(
    (f) => f.field === "date" && f.op === "gte",
  );
  const dateToFilter = filters.find(
    (f) => f.field === "date" && f.op === "lte",
  );

  const [templateId, setTemplateId] = useState<string>(
    (templateFilter?.value as string) || "",
  );
  const [roomId, setRoomId] = useState<string>(
    (roomFilter?.value as string) || "",
  );
  const [instructorId, setInstructorId] = useState<string>(
    (instructorFilter?.value as string) || "",
  );

  const selectedDays = useMemo(
    () => getDayOfWeekFilterValues(filters),
    [filters],
  );

  const fromStr =
    dateFromFilter?.value != null
      ? String(dateFromFilter.value).split(" ")[0]
      : undefined;
  const toStr =
    dateToFilter?.value != null
      ? String(dateToFilter.value).split(" ")[0]
      : undefined;
  const [fromDate, setFromDate] = useState<Date | undefined>(
    fromStr ? new Date(fromStr) : undefined,
  );
  const [toDate, setToDate] = useState<Date | undefined>(
    toStr ? new Date(toStr) : undefined,
  );

  useEffect(
    () => setTemplateId((templateFilter?.value as string) || ""),
    [templateFilter?.value],
  );
  useEffect(
    () => setRoomId((roomFilter?.value as string) || ""),
    [roomFilter?.value],
  );
  useEffect(
    () => setInstructorId((instructorFilter?.value as string) || ""),
    [instructorFilter?.value],
  );
  useEffect(() => {
    const s =
      dateFromFilter?.value != null
        ? String(dateFromFilter.value).split(" ")[0]
        : undefined;
    setFromDate(s ? new Date(s) : undefined);
  }, [dateFromFilter?.value]);
  useEffect(() => {
    const s =
      dateToFilter?.value != null
        ? String(dateToFilter.value).split(" ")[0]
        : undefined;
    setToDate(s ? new Date(s) : undefined);
  }, [dateToFilter?.value]);

  const statusValue =
    typeof statusFilter?.value === "string" ? statusFilter.value : "";

  const hasFilter =
    !!templateId ||
    selectedDays.length > 0 ||
    !!classTypeFilter?.value ||
    !!levelFilter?.value ||
    !!roomId ||
    !!instructorId ||
    !!statusValue ||
    !!fromDate ||
    !!toDate;

  const pushFilters = (nextFilters: FilterConditionDTO[] | undefined) => {
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const toggleDay = (day: number) => {
    const nextDays = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day].sort((a, b) => a - b);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    nextFilters = removeConditions(nextFilters, "dayOfWeek");
    if (nextDays.length > 0) {
      nextFilters = upsertCondition(nextFilters, {
        field: "dayOfWeek",
        op: "in",
        value: nextDays,
      });
    }
    pushFilters(nextFilters);
  };

  return (
    <div className="mb-4 flex flex-col gap-3 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <InfiniteCombobox<ClassTemplateItem>
            queryKey={["classes", "templates", "lookup", "session-filter"]}
            queryFn={async ({ search: q, pageParam }) => {
              const limit = 20;
              const skip = (pageParam - 1) * limit;
              const url = new URL(`${config.apiUrl}/classes/templates/lookup`);
              url.searchParams.set("limit", String(limit));
              url.searchParams.set("skip", String(skip));
              if (q) url.searchParams.set("q", q);
              const data = await fetcher.get<{
                items: Array<{ id: string; name: string }>;
                total: number;
              }>(url.toString());
              return {
                items: data.items.map((t) => ({ id: t.id, name: t.name })),
                nextPage: skip + limit < data.total ? pageParam + 1 : null,
              };
            }}
            preloadQueryFn={(id) =>
              hydrateLookupItem<ClassTemplateItem>(
                `${config.apiUrl}/classes/templates/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={templateId}
            onValueChange={(val) => {
              setTemplateId(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "templateId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "templateId");
              }
              pushFilters(nextFilters);
            }}
            placeholder="ແມ່ແບບ"
            className="h-8 w-full sm:w-52"
            clearable
          />

          <InfiniteCombobox<RoomItem>
            queryKey={["master", "rooms", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<RoomItem>(
                `${config.apiUrl}/master/rooms/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<RoomItem>(
                `${config.apiUrl}/master/rooms/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={roomId}
            onValueChange={(val) => {
              setRoomId(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "roomId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "roomId");
              }
              pushFilters(nextFilters);
            }}
            placeholder="ຫ້ອງ"
            className="h-8 w-full sm:w-48"
            clearable
          />

          <InfiniteCombobox<InstructorItem>
            queryKey={["master", "instructors", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<InstructorItem>(
                `${config.apiUrl}/master/instructors/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<InstructorItem>(
                `${config.apiUrl}/master/instructors/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={instructorId}
            onValueChange={(val) => {
              setInstructorId(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "instructorId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "instructorId");
              }
              pushFilters(nextFilters);
            }}
            placeholder="ຄູຜູ້ສອນ"
            className="h-8 w-full sm:w-48"
            clearable
          />

          <DatePicker
            mode="range"
            value={{
              from: fromDate,
              to: toDate,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : range?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : range?.to;
              setFromDate(from);
              setToDate(to);
              const fromStrVal = from ? format(from, "yyyy-MM-dd") : "";
              const toStrVal = to ? format(to, "yyyy-MM-dd") : "";
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              nextFilters = removeConditions(nextFilters, "date");
              const dateRange: FilterConditionDTO[] = [];
              if (fromStrVal) {
                dateRange.push({
                  field: "date",
                  op: "gte",
                  value: fromStrVal,
                });
              }
              if (toStrVal) {
                dateRange.push({
                  field: "date",
                  op: "lte",
                  value: toStrVal,
                });
              }
              if (dateRange.length > 0) {
                nextFilters = upsertCondition(nextFilters, {
                  field: "date",
                  op: "and",
                  value: dateRange as FilterConditionDTO["value"],
                });
              }
              pushFilters(nextFilters);
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />

          <SimpleSelect
            value={statusValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              pushFilters(nextFilters);
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "scheduled", label: "ຈອງແລ້ວ" },
              { value: "completed", label: "ເຂົ້າຮຽນແລ້ວ" },
              { value: "cancelled", label: "ຍົກເລີກ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              nav({ search: { ...search, filters: undefined, offset: 0 } });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs">ວັນ:</span>
        {CLASS_SESSION_DAYS_OF_WEEK.map((day) => {
          const active = selectedDays.includes(day.value);
          return (
            <Button
              key={day.value}
              type="button"
              size="sm"
              variant={active ? "default" : "outline"}
              className={cn("h-8 min-w-12 px-3", active && "shadow-sm")}
              onClick={() => toggleDay(day.value)}
            >
              {day.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export function useClassSessionFilterSummary(
  filters: FilterConditionDTO[] | undefined,
) {
  const stableFilters = useMemo(
    () => filters ?? EMPTY_FILTERS,
    [filters],
  );

  return useMemo(() => {
    const days = getDayOfWeekFilterValues(stableFilters);
    const dayLabels =
      days.length > 0
        ? CLASS_SESSION_DAYS_OF_WEEK.filter((d) => days.includes(d.value))
            .map((d) => d.label)
            .join(", ")
        : undefined;
    const hasFilter = stableFilters.length > 0;
    return { days, dayLabels, hasFilter };
  }, [stableFilters]);
}
